import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Users, Package, IndianRupee, UserCheck, AlertTriangle, Bell, Zap, Phone } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const STAGE_COLORS = { New: '#60A5FA', Interested: '#C8A96E', Callback: '#FBBF24', Converted: '#4ADE80', Lost: '#F87171' };

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [assignResult, setAssignResult] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, alertsRes] = await Promise.all([
        api.getDashboardStats(),
        api.getRenewalAlerts()
      ]);
      setStats(statsRes.data);
      setAlerts(alertsRes.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAutoAssign = async () => {
    setAssigning(true);
    try {
      const res = await api.autoAssignDeliveryBoys();
      setAssignResult(res.data);
      setTimeout(() => setAssignResult(null), 5000);
    } catch (err) { console.error(err); }
    setAssigning(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64" style={{ color: '#A8A29E' }}>Loading dashboard...</div>;
  if (!stats) return <div className="text-center py-12" style={{ color: '#A8A29E' }}>Failed to load dashboard</div>;

  const pipelineData = Object.entries(stats.stages || {}).map(([name, value]) => ({ name, value }));
  const pieData = (stats.sources || []).map(s => ({ name: s.source, value: s.count }));
  const PIE_COLORS = ['#C8A96E', '#D4AF37', '#60A5FA', '#4ADE80', '#FBBF24', '#F87171', '#A855F7', '#2DD4BF'];

  const MetricCard = ({ icon: Icon, label, value, sub, color }) => (
    <div className="rounded-xl p-6 transition-all duration-200 hover:-translate-y-1" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.2)' }}>
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${color}15` }}>
          <Icon className="w-5 h-5" style={{ color, strokeWidth: 1.5 }} />
        </div>
      </div>
      <div className="text-2xl font-bold" style={{ fontFamily: 'Outfit', color: '#F5F5F4' }}>{value}</div>
      <div className="text-sm mt-1" style={{ color: '#A8A29E' }}>{label}</div>
      {sub && <div className="text-xs mt-1" style={{ color: '#78716C' }}>{sub}</div>}
    </div>
  );

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="rounded-lg px-3 py-2 text-sm" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.2)', color: '#F5F5F4' }}>
        <p className="font-medium">{label}: {payload[0].value}</p>
      </div>
    );
  };

  return (
    <div data-testid="dashboard-panel" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit', color: '#F5F5F4' }}>Dashboard</h2>
          <p className="text-sm mt-1" style={{ color: '#A8A29E' }}>Overview of your millet subscription business</p>
        </div>
        <button data-testid="auto-assign-button" onClick={handleAutoAssign} disabled={assigning}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all"
          style={{ background: '#2A241F', color: '#C8A96E', border: '1px solid rgba(200,169,110,0.2)' }}>
          <Zap className="w-4 h-4" /> {assigning ? 'Assigning...' : 'Auto-Assign Delivery Boys'}
        </button>
      </div>

      {/* Auto-assign result */}
      {assignResult && (
        <div className="rounded-xl p-4 text-sm fade-in" style={{ background: 'rgba(74,222,128,0.05)', border: '1px solid rgba(74,222,128,0.2)', color: '#4ADE80' }}>
          {assignResult.message}
          {assignResult.unassigned?.length > 0 && (
            <span style={{ color: '#FBBF24' }}> | {assignResult.unassigned.length} unmatched (no matching delivery boy area)</span>
          )}
        </div>
      )}

      {/* Renewal Alerts */}
      {alerts.length > 0 && (
        <div data-testid="renewal-alerts" className="rounded-xl overflow-hidden" style={{ background: '#1E1A17', border: '1px solid rgba(248,113,113,0.2)' }}>
          <div className="p-4 flex items-center gap-2" style={{ borderBottom: '1px solid #2A241F' }}>
            <Bell className="w-4 h-4" style={{ color: '#F87171' }} />
            <h3 className="text-sm font-semibold" style={{ fontFamily: 'Outfit', color: '#F87171' }}>Renewal Alerts ({alerts.length})</h3>
          </div>
          <div className="divide-y" style={{ borderColor: '#2A241F' }}>
            {alerts.slice(0, 10).map((alert, i) => (
              <div key={alert.id} className="px-4 py-3 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{
                  background: alert.severity === 'critical' ? 'rgba(248,113,113,0.15)' : alert.severity === 'urgent' ? 'rgba(251,191,36,0.15)' : 'rgba(200,169,110,0.15)'
                }}>
                  <AlertTriangle className="w-4 h-4" style={{
                    color: alert.severity === 'critical' ? '#F87171' : alert.severity === 'urgent' ? '#FBBF24' : '#C8A96E'
                  }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium" style={{ color: '#F5F5F4' }}>{alert.name}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                      alert.severity === 'critical' ? 'badge-lost' : alert.severity === 'urgent' ? 'badge-callback' : 'badge-interested'
                    }`}>
                      {alert.daysLeft <= 0 ? 'Expired' : `${alert.daysLeft} days left`}
                    </span>
                  </div>
                  <div className="text-xs mt-0.5" style={{ color: '#78716C' }}>
                    {alert.apt}{alert.flat ? ` / ${alert.flat}` : ''} &middot; {alert.planName || 'Subscription'}
                  </div>
                </div>
                <a href={`tel:${alert.mobile}`} className="flex-shrink-0 p-2 rounded-lg transition-all" style={{ color: '#C8A96E', background: 'rgba(200,169,110,0.1)' }}>
                  <Phone className="w-4 h-4" />
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard icon={Users} label="Total Leads" value={stats.totalLeads} sub={`${stats.converted} converted (${stats.totalLeads ? Math.round(stats.converted / stats.totalLeads * 100) : 0}%)`} color="#60A5FA" />
        <MetricCard icon={UserCheck} label="Active Subscribers" value={stats.activeSubscribers} sub={`${stats.ootSubscribers} out of town`} color="#4ADE80" />
        <MetricCard icon={IndianRupee} label="Monthly Revenue" value={`${stats.mrr.toLocaleString('en-IN')}`} sub="MRR from active subs" color="#C8A96E" />
        <MetricCard icon={Package} label="Today's Deliveries" value={stats.todayDeliveries} sub={stats.renewalCritical > 0 ? `${stats.renewalCritical} expired subs!` : "orders to go out"} color="#FBBF24" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-xl p-6" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.2)' }}>
          <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Outfit', color: '#F5F5F4' }}>Pipeline by Stage</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={pipelineData}>
                <XAxis dataKey="name" tick={{ fill: '#A8A29E', fontSize: 12 }} axisLine={{ stroke: '#2A241F' }} tickLine={false} />
                <YAxis tick={{ fill: '#A8A29E', fontSize: 12 }} axisLine={{ stroke: '#2A241F' }} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {pipelineData.map((entry) => (
                    <Cell key={entry.name} fill={STAGE_COLORS[entry.name] || '#C8A96E'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-xl p-6" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.2)' }}>
          <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Outfit', color: '#F5F5F4' }}>Leads by Source</h3>
          {pieData.length > 0 ? (
            <div className="h-64 flex items-center">
              <ResponsiveContainer width="50%" height="100%">
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" outerRadius={80} dataKey="value" strokeWidth={0}>
                    {pieData.map((entry) => <Cell key={entry.name} fill={PIE_COLORS[pieData.indexOf(entry) % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2 flex-1">
                {pieData.map((s) => (
                  <div key={s.name} className="flex items-center gap-2 text-sm">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: PIE_COLORS[pieData.indexOf(s) % PIE_COLORS.length] }} />
                    <span className="truncate" style={{ color: '#A8A29E' }}>{s.name}</span>
                    <span className="ml-auto font-medium" style={{ color: '#F5F5F4' }}>{s.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center" style={{ color: '#78716C' }}>No lead data yet</div>
          )}
        </div>
      </div>

      {stats.apartments?.length > 0 && (
        <div className="rounded-xl overflow-hidden" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.2)' }}>
          <div className="p-6 pb-4">
            <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit', color: '#F5F5F4' }}>Apartment Breakdown</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full gm-table">
              <thead>
                <tr><th>Apartment</th><th>Active</th><th>Out of town</th><th>Paused</th><th>Total Subs</th><th>MRR</th></tr>
              </thead>
              <tbody>
                {stats.apartments.map((a) => (
                  <tr key={a.apartment}>
                    <td style={{ color: '#F5F5F4' }}>{a.apartment}</td>
                    <td>{a.active}</td>
                    <td>{a.oot}</td>
                    <td>{a.paused}</td>
                    <td>{a.total}</td>
                    <td style={{ color: '#C8A96E' }}>{a.mrr.toLocaleString('en-IN')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
